import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu, RefreshCw } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLeads } from "@/context/LeadContext";
import { adminLogout } from "@/integrations/php/client";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ThemeSwitcher } from "../ui/theme-switcher";
import AdminSidebar from "../sidebar/AdminSidebar";

const routeTitle = (pathname: string): string => {
  if (pathname === "/admin") return "Γενική εικόνα";
  if (pathname.startsWith("/admin/operations")) return "Operations Inbox";
  if (pathname.startsWith("/admin/partner-care")) return "Partner Care";
  if (pathname.startsWith("/admin/newsletter")) return "Newsletter";
  if (pathname.startsWith("/admin/partners")) return "Δίκτυο συνεργατών";
  if (pathname.startsWith("/admin/leads/meta")) return "Meta Lead Hub";
  if (pathname === "/admin/leads/new") return "Νέα χειροκίνητη επαφή";
  if (pathname === "/admin/leads/assignment") return "Κατανομή αναθέσεων";
  if (pathname.startsWith("/admin/leads")) return "Διαχείριση leads";
  if (pathname.startsWith("/admin/messages/statistics")) return "Στατιστικά SMS";
  if (pathname.startsWith("/admin/email/statistics")) return "Στατιστικά email";
  if (pathname.startsWith("/admin/messages")) return "Επικοινωνία";
  if (pathname.startsWith("/admin/reports")) return "Αναφορές";
  if (pathname.startsWith("/admin/billing")) return "Χρεώσεις";
  if (pathname.startsWith("/admin/settings")) return "Ρυθμίσεις";
  if (pathname.startsWith("/admin/changelog")) return "Lineweb Updates";
  return "Πίνακας διαχείρισης";
};

const AdminHeader: React.FC = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const { newLeadsCount, unassignedCount, markLeadsAsSeen, refreshLeads, lastRefreshedAt } = useLeads();
  const username = sessionStorage.getItem("adminUsername") || "Admin";

  const handleBellClick = () => {
    markLeadsAsSeen();
    navigate(unassignedCount > 0 ? "/admin/operations" : "/admin/leads");
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshLeads();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
      <div className="flex h-16 items-center justify-between gap-3 px-3 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          {isMobile && (
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0" aria-label="Άνοιγμα μενού">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[88vw] max-w-80 border-none p-0">
                <SheetTitle className="sr-only">Κύρια πλοήγηση διαχείρισης</SheetTitle>
                <SheetDescription className="sr-only">
                  Επιλέξτε ενότητα του πίνακα διαχείρισης.
                </SheetDescription>
                <AdminSidebar onNavigate={() => setMenuOpen(false)} />
              </SheetContent>
            </Sheet>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900 sm:text-base">{routeTitle(location.pathname)}</p>
            {lastRefreshedAt && (
              <p className="hidden items-center gap-1.5 text-xs text-slate-400 sm:flex">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Δεδομένα {formatDistanceToNow(lastRefreshedAt, { addSuffix: true, locale: el })}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <ThemeSwitcher />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 text-slate-500 hover:text-slate-900"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                aria-label="Ανανέωση δεδομένων"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Ανανέωση πρόσφατων δεδομένων</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative h-11 w-11 text-slate-500 hover:text-slate-900"
                onClick={handleBellClick}
                aria-label="Εκκρεμότητες"
              >
                <Bell className="h-5 w-5" />
                {(unassignedCount > 0 || newLeadsCount > 0) && (
                  <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-bold text-white ring-2 ring-white">
                    {(unassignedCount || newLeadsCount) > 9 ? "9+" : unassignedCount || newLeadsCount}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {unassignedCount > 0 ? `${unassignedCount} leads χωρίς ανάθεση` : `${newLeadsCount} νέα leads`}
            </TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-11 gap-2 rounded-xl px-1.5 sm:px-2.5">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-slate-900 text-xs font-semibold text-white">
                    {username.slice(0, 1).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden max-w-28 truncate text-sm font-medium text-slate-700 md:inline">{username}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Λογαριασμός διαχειριστή</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer text-rose-700" onClick={adminLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Αποσύνδεση
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
